import { BaseNodeCommand, type NodeChangeRecord } from './base/BaseNodeCommand';
import { useDiagramStore } from '../../stores/diagramStore';
import { useTableCellStore, type TableCellSelectionState } from '../../stores/tableCellStore';
import { computeDockedLayoutUpdatesForTable } from '../../domains/diagram/shapes/table/tableDockLayout.utils';
import { SessionMetrics, TABLE_SESSION_METRICS } from '../../domains/analytics';

type Axis = 'row' | 'col';

type MergedRegion = { startRow: number; startCol: number; endRow: number; endCol: number };

const clampCount = (value: unknown, fallback: number, min = 1): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.max(min, Math.floor(value));
  return fallback;
};

const uniqSorted = (arr: number[]) => Array.from(new Set(arr.filter((n) => Number.isFinite(n)))).sort((a, b) => a - b);

const parseCellKey = (key: unknown): { r: number; c: number } | null => {
  if (typeof key !== 'string') return null;
  const [rStr, cStr] = key.split(',');
  const r = Number(rStr);
  const c = Number(cStr);
  if (!Number.isFinite(r) || !Number.isFinite(c)) return null;
  return { r, c };
};

const makeCellKey = (r: number, c: number) => `${r},${c}` as `${number},${number}`;

const remapSelection = (
  selection: TableCellSelectionState | undefined,
  axis: Axis,
  newIndexByOldIndex: number[],
): TableCellSelectionState | undefined => {
  if (!selection) return undefined;

  const remapKey = (key: unknown) => {
    const parsed = parseCellKey(key);
    if (!parsed) return null;
    const nr = axis === 'row' ? newIndexByOldIndex[parsed.r] : parsed.r;
    const nc = axis === 'col' ? newIndexByOldIndex[parsed.c] : parsed.c;
    if (!Number.isFinite(nr) || !Number.isFinite(nc)) return null;
    return makeCellKey(nr, nc);
  };

  const nextAnchor = selection.anchorKey ? remapKey(selection.anchorKey) : null;
  const nextSelected = (selection.selectedKeys ?? [])
    .map((k) => remapKey(k))
    .filter((k): k is `${number},${number}` => typeof k === 'string');

  if (nextSelected.length === 0 && !nextAnchor) return undefined;
  return {
    anchorKey: (nextAnchor ?? nextSelected[0]) || undefined,
    selectedKeys: nextSelected,
  };
};

const buildNewIndexByOldIndexForMove = (count: number, from: number, to: number): number[] => {
  const order = Array.from({ length: count }, (_, i) => i);
  const [moved] = order.splice(from, 1);
  order.splice(to, 0, moved);
  const newByOld = Array(count).fill(0);
  for (let newIdx = 0; newIdx < order.length; newIdx += 1) {
    const oldIdx = order[newIdx];
    newByOld[oldIdx] = newIdx;
  }
  return newByOld;
};

const remapCellMapKeys = <T extends Record<string, any>>(
  map: T,
  axis: Axis,
  newIndexByOldIndex: number[],
): Record<string, any> => {
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(map ?? {})) {
    const parsed = parseCellKey(k);
    if (!parsed) continue;
    const r = parsed.r;
    const c = parsed.c;
    if (axis === 'row') {
      const nr = newIndexByOldIndex[r];
      if (typeof nr !== 'number') continue;
      out[makeCellKey(nr, c)] = v;
    } else {
      const nc = newIndexByOldIndex[c];
      if (typeof nc !== 'number') continue;
      out[makeCellKey(r, nc)] = v;
    }
  }
  return out;
};

const remapMergedRegions = (regions: unknown, axis: Axis, newIndexByOldIndex: number[]): MergedRegion[] => {
  if (!Array.isArray(regions)) return [];
  const out: MergedRegion[] = [];
  for (const r of regions) {
    if (!r || typeof r !== 'object') continue;
    const mr = r as any;
    const startRow = Number(mr.startRow);
    const endRow = Number(mr.endRow);
    const startCol = Number(mr.startCol);
    const endCol = Number(mr.endCol);
    if (![startRow, endRow, startCol, endCol].every((n) => Number.isFinite(n))) continue;

    if (axis === 'row') {
      const a = newIndexByOldIndex[startRow];
      const b = newIndexByOldIndex[endRow];
      const ns = Math.min(a, b);
      const ne = Math.max(a, b);
      out.push({ startRow: ns, endRow: ne, startCol, endCol });
    } else {
      const a = newIndexByOldIndex[startCol];
      const b = newIndexByOldIndex[endCol];
      const ns = Math.min(a, b);
      const ne = Math.max(a, b);
      out.push({ startRow, endRow, startCol: ns, endCol: ne });
    }
  }
  return out;
};

export const isTableAxisReorderMergeSafe = (
  regions: unknown,
  axis: Axis,
  count: number,
  fromIndex: number,
  toIndex: number,
): boolean => {
  if (!Array.isArray(regions)) return true;
  const newIndexByOldIndex = buildNewIndexByOldIndexForMove(count, fromIndex, toIndex);
  for (const r of regions) {
    if (!r || typeof r !== 'object') continue;
    const mr = r as any;
    const startRow = Number(mr.startRow);
    const endRow = Number(mr.endRow);
    const startCol = Number(mr.startCol);
    const endCol = Number(mr.endCol);
    if (![startRow, endRow, startCol, endCol].every((n) => Number.isFinite(n))) continue;

    const start = axis === 'row' ? startRow : startCol;
    const end = axis === 'row' ? endRow : endCol;
    const remapped = Array.from({ length: end - start + 1 }, (_, offset) => newIndexByOldIndex[start + offset])
      .filter((index): index is number => Number.isFinite(index))
      .sort((a, b) => a - b);
    if (remapped.length !== end - start + 1) return false;
    if (remapped[remapped.length - 1] - remapped[0] + 1 !== remapped.length) return false;
  }
  return true;
};

export class ReorderTableAxisCommand extends BaseNodeCommand {
  private selectionBefore?: TableCellSelectionState;
  private selectionAfter?: TableCellSelectionState;

  constructor(
    private tableId: string,
    private axis: Axis,
    private fromIndex: number,
    private toIndex: number,
  ) {
    super('Reorder Table Axis', `${axis} ${fromIndex} → ${toIndex}`);
  }

  execute(): void {
    const mutationManager = this.getMutationManager() as any;
    const table = mutationManager.getNode(this.tableId) as any;
    if (!table || table.type !== 'table') {
      super.execute();
      return;
    }

    const attrs = (table.attrs ?? {}) as Record<string, unknown>;
    const rowCount = clampCount(attrs.rowCount, 3);
    const colCount = clampCount(attrs.columnCount, 3);
    const showHeader = attrs.showHeader !== false;

    const rowSegments = showHeader ? rowCount + 1 : rowCount;
    const colSegments = colCount;
    const count = this.axis === 'row' ? rowSegments : colSegments;
    const newIndexByOldIndex = buildNewIndexByOldIndexForMove(count, this.fromIndex, this.toIndex);

    const cellState = useTableCellStore.getState();
    this.selectionBefore = cellState.selections[this.tableId];
    this.selectionAfter = remapSelection(this.selectionBefore, this.axis, newIndexByOldIndex);

    super.execute();

    if (this.selectionAfter) {
      useTableCellStore.getState().setSelection(this.tableId, this.selectionAfter);
    } else {
      useTableCellStore.getState().clearSelection(this.tableId);
    }
  }

  undo(): void {
    super.undo();
    if (this.selectionBefore) {
      useTableCellStore.getState().setSelection(this.tableId, this.selectionBefore);
    } else {
      useTableCellStore.getState().clearSelection(this.tableId);
    }
  }

  redo(): void {
    super.redo();
    if (this.selectionAfter) {
      useTableCellStore.getState().setSelection(this.tableId, this.selectionAfter);
    } else {
      useTableCellStore.getState().clearSelection(this.tableId);
    }
  }

  protected doExecute(): NodeChangeRecord[] {
    const mutationManager = this.getMutationManager() as any;
    const table = mutationManager.getNode(this.tableId) as any;
    if (!table || table.type !== 'table') return [];

    const attrs = (table.attrs ?? {}) as Record<string, unknown>;
    const rowCount = clampCount(attrs.rowCount, 3);
    const colCount = clampCount(attrs.columnCount, 3);
    const showHeader = attrs.showHeader !== false;

    const rowSegments = showHeader ? rowCount + 1 : rowCount;
    const colSegments = colCount;
    const count = this.axis === 'row' ? rowSegments : colSegments;

    if (count <= 1) return [];
    if (!Number.isFinite(this.fromIndex) || !Number.isFinite(this.toIndex)) return [];
    if (this.fromIndex < 0 || this.fromIndex >= count) return [];
    if (this.toIndex < 0 || this.toIndex >= count) return [];
    if (this.fromIndex === this.toIndex) return [];

    const mergedRegions = (attrs as any).mergedRegions;
    if (!isTableAxisReorderMergeSafe(mergedRegions, this.axis, count, this.fromIndex, this.toIndex)) return [];

    const newIndexByOldIndex = buildNewIndexByOldIndexForMove(count, this.fromIndex, this.toIndex);

    const changes: any = { attrs: {} as Record<string, unknown> };

    if (this.axis === 'row') {
      const headerHeight = typeof attrs.headerHeight === 'number' && Number.isFinite(attrs.headerHeight) && attrs.headerHeight > 0
        ? (attrs.headerHeight as number)
        : 32;
      const rowHeights = Array.isArray((attrs as any).rowHeights) ? ([...(attrs as any).rowHeights] as number[]) : Array(rowCount).fill(20);
      const combined = showHeader ? [headerHeight, ...rowHeights] : [...rowHeights];

      const nextCombined = Array(combined.length).fill(0) as number[];
      for (let oldIdx = 0; oldIdx < combined.length; oldIdx += 1) {
        const newIdx = newIndexByOldIndex[oldIdx];
        nextCombined[newIdx] = combined[oldIdx];
      }

      if (showHeader) {
        changes.attrs.headerHeight = nextCombined[0];
        changes.attrs.rowHeights = nextCombined.slice(1);
      } else {
        changes.attrs.rowHeights = nextCombined;
      }

      const prevHeaderRows = Array.isArray((attrs as any).headerRowIndices) ? ([...(attrs as any).headerRowIndices] as number[]) : [];
      const remappedHeaderRows = uniqSorted(prevHeaderRows.map((i) => newIndexByOldIndex[i]).filter((n) => typeof n === 'number'));
      changes.attrs.headerRowIndices = showHeader ? uniqSorted([0, ...remappedHeaderRows]) : remappedHeaderRows;
    } else {
      const colWidths = Array.isArray((attrs as any).columnWidths) ? ([...(attrs as any).columnWidths] as number[]) : Array(colCount).fill(20);
      const nextWidths = Array(colWidths.length).fill(0) as number[];
      for (let oldIdx = 0; oldIdx < colWidths.length; oldIdx += 1) {
        const newIdx = newIndexByOldIndex[oldIdx];
        nextWidths[newIdx] = colWidths[oldIdx];
      }
      changes.attrs.columnWidths = nextWidths;

      const showHeaderColumn = (attrs as any).showHeaderColumn === true;
      const prevHeaderCols = Array.isArray((attrs as any).headerColIndices) ? ([...(attrs as any).headerColIndices] as number[]) : [];
      const remappedHeaderCols = uniqSorted(prevHeaderCols.map((i) => newIndexByOldIndex[i]).filter((n) => typeof n === 'number'));
      changes.attrs.headerColIndices = showHeaderColumn ? uniqSorted([0, ...remappedHeaderCols]) : remappedHeaderCols;
    }

    const cellText = ((attrs as any).cellText ?? {}) as Record<string, string>;
    const cellStyles = ((attrs as any).cellStyles ?? {}) as Record<string, unknown>;
    const cellLayouts = ((attrs as any).cellLayouts ?? {}) as Record<string, unknown>;

    changes.attrs.cellText = remapCellMapKeys(cellText, this.axis, newIndexByOldIndex);
    changes.attrs.cellStyles = remapCellMapKeys(cellStyles, this.axis, newIndexByOldIndex);
    changes.attrs.cellLayouts = remapCellMapKeys(cellLayouts, this.axis, newIndexByOldIndex);
    changes.attrs.mergedRegions = remapMergedRegions(mergedRegions, this.axis, newIndexByOldIndex);

    const latest = mutationManager.getNode(this.tableId);
    changes.x = (latest as any)?.x ?? table.x ?? 0;
    changes.y = (latest as any)?.y ?? table.y ?? 0;

    const previewAttrs = { ...(attrs as any), ...(changes.attrs as any) };
    const previewTable: any = { ...table, ...changes, attrs: previewAttrs };

    const storeNodes = useDiagramStore.getState().nodes;
    const dockedToTable = storeNodes.filter((n) => (n as any)?.attrs?.tableDock?.tableId === this.tableId);

    const previewNodes = storeNodes.map((n) => {
      const dock = (n as any)?.attrs?.tableDock;
      if (!dock || dock.tableId !== this.tableId) return n;
      const parsed = parseCellKey(dock.cellKey);
      if (!parsed) return n;
      const nr = this.axis === 'row' ? newIndexByOldIndex[parsed.r] : parsed.r;
      const nc = this.axis === 'col' ? newIndexByOldIndex[parsed.c] : parsed.c;
      const nextDock = { ...dock, cellKey: makeCellKey(nr, nc) };
      return { ...n, attrs: { ...(n as any).attrs, tableDock: nextDock } } as any;
    });

    const layoutUpdates = computeDockedLayoutUpdatesForTable(previewTable, previewNodes);
    const layoutById = new Map(layoutUpdates.map((u) => [u.nodeId, u.changes]));

    const batch: Array<{ nodeId: string; changes: any }> = [{ nodeId: this.tableId, changes }];

    for (const n of dockedToTable) {
      const dock = (n as any)?.attrs?.tableDock;
      const parsed = parseCellKey(dock?.cellKey);
      if (!dock || !parsed) continue;
      const nr = this.axis === 'row' ? newIndexByOldIndex[parsed.r] : parsed.r;
      const nc = this.axis === 'col' ? newIndexByOldIndex[parsed.c] : parsed.c;
      const nextDock = { ...dock, cellKey: makeCellKey(nr, nc) };
      const layout = layoutById.get(n.id) ?? {};
      batch.push({ nodeId: n.id, changes: { ...layout, attrs: { tableDock: nextDock } } });
    }

    if (batch.length > 1 && typeof mutationManager.applyBatchNodeChanges === 'function') {
      const records = mutationManager.applyBatchNodeChanges(batch) ?? [];
      if (records.length > 0) {
        SessionMetrics.increment(TABLE_SESSION_METRICS.REORDERS);
      }
      return records;
    }

    const records: NodeChangeRecord[] = [];
    for (const b of batch) {
      const rec = mutationManager.applyNodeChange(b.nodeId, b.changes);
      if (rec) records.push(rec);
    }
    if (records.length > 0) {
      SessionMetrics.increment(TABLE_SESSION_METRICS.REORDERS);
    }
    return records;
  }
}
